import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import Button from 'components/Button';
import { CompanyWithOwnership } from 'types/company';
import { useCompanyServiceQueries } from 'services/company';
import {
  TIN_PLACEHOLDER,
  validateTin,
  formatTinForInput,
} from 'utils/tinUtils';
import { companyVatStatusSelectOptions } from 'utils/companyVatStatusOptions';

const MONTH_DAY_PATTERN = /^\d{2}-\d{2}$/;

const toMonthDay = (value: string): string => {
  if (value && value.length >= 10) {
    return value.slice(5, 10);
  }
  return value;
};

const toFullDate = (monthDay: string, year: number): string => {
  if (!MONTH_DAY_PATTERN.test(monthDay)) return '';
  return `${year}-${monthDay}`;
};

const MONTH_OPTIONS = [
  { label: 'Jan', value: '01' },
  { label: 'Feb', value: '02' },
  { label: 'Mar', value: '03' },
  { label: 'Apr', value: '04' },
  { label: 'May', value: '05' },
  { label: 'Jun', value: '06' },
  { label: 'Jul', value: '07' },
  { label: 'Aug', value: '08' },
  { label: 'Sep', value: '09' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' },
];

const splitMonthDay = (value: string): { month: string; day: string } => {
  if (!MONTH_DAY_PATTERN.test(value)) {
    return { month: '', day: '' };
  }
  const [month, day] = value.split('-');
  return { month, day };
};

const buildMonthDay = (month: string, day: string): string => {
  if (!month) return '';
  if (!day) return `${month}-01`;
  return `${month}-${day}`;
};

const REGISTRATION_OPTIONS = [
  {
    label: 'Sole Proprietorship',
    value: 'sole_proprietorship',
    registrationEntity: 'DTI',
    idPlaceholder: 'DTI Business Name Registration Number',
  },
  {
    label: 'One Person Corporation (OPC)',
    value: 'opc',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Corporation (Stock/Non-Stock)',
    value: 'corporation',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Partnership (General/Limited)',
    value: 'partnership',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Cooperative',
    value: 'cooperative',
    registrationEntity: 'CDA',
    idPlaceholder: 'CDA Registration Number',
  },
] as const;
const DEFAULT_COMPANY_DESCRIPTION = 'No description provided.';

interface EditCompanyModalProps {
  show: boolean;
  onHide: () => void;
  company: CompanyWithOwnership | null;
  onSuccess: (updated: CompanyWithOwnership) => void;
  onError: (message: string) => void;
}

function EditCompanyModal({
  show,
  onHide,
  company,
  onSuccess,
  onError,
}: EditCompanyModalProps) {
  const currentYear = new Date().getFullYear();
  const defaultFiscalYearStart = `${currentYear}-01-01`;
  const defaultFiscalYearEnd = `${currentYear}-12-31`;
  const companyService = useCompanyServiceQueries();
  const [form, setForm] = useState<{
    name: string;
    description: string;
    address: string;
    tin: string;
    tel: string;
    email: string;
    vatStatus: string;
    entityType: string;
    registrationDate: string;
    registrationNumber: string;
    tradeName: string;
    businessStyle: string;
    isBmbe: boolean;
    bmbeRegistrationNumber: string;
    fiscalYearType: 'calendar' | 'custom';
    fiscalYearStart: string;
    fiscalYearEnd: string;
  }>({
    name: '',
    description: '',
    address: '',
    tin: '',
    tel: '',
    email: '',
    vatStatus: '',
    entityType: '',
    registrationDate: '',
    registrationNumber: '',
    tradeName: '',
    businessStyle: '',
    isBmbe: false,
    bmbeRegistrationNumber: '',
    fiscalYearType: 'calendar',
    fiscalYearStart: '01-01',
    fiscalYearEnd: '12-31',
  });
  const [submitting, setSubmitting] = useState(false);
  const [tinError, setTinError] = useState<string | null>(null);
  const selectedRegistrationOption = REGISTRATION_OPTIONS.find(
    (option) => option.value === form.entityType,
  );
  const registrationNumberLabel =
    selectedRegistrationOption?.idPlaceholder ||
    'SEC Registration No. or DTI Certificate No.';

  const vatStatusOptions = useMemo(
    () => companyVatStatusSelectOptions(form.vatStatus),
    [form.vatStatus],
  );

  const getTinValidationError = useCallback(validateTin, []);

  const formatTinForInputMemo = useCallback(formatTinForInput, []);
  const validateMonthDay = useCallback(
    (value: string): boolean => {
      if (!MONTH_DAY_PATTERN.test(value)) return false;
      const [monthString, dayString] = value.split('-');
      const month = Number(monthString);
      const day = Number(dayString);
      if (month < 1 || month > 12) return false;
      const daysInMonth = new Date(currentYear, month, 0).getDate();
      return day >= 1 && day <= daysInMonth;
    },
    [currentYear],
  );
  const getDayOptions = useCallback(
    (month: string): string[] => {
      if (!month) return [];
      const monthNumber = Number(month);
      if (monthNumber < 1 || monthNumber > 12) return [];
      const daysInMonth = new Date(currentYear, monthNumber, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, index) =>
        String(index + 1).padStart(2, '0'),
      );
    },
    [currentYear],
  );

  useEffect(() => {
    if (company && show) {
      setForm({
        name: company.name || '',
        description: company.description || '',
        address: company.address || '',
        tin: company.tin || '',
        tel: company.tel || '',
        email: company.email || '',
        vatStatus: company.vatStatus || '',
        entityType: company.entityType || '',
        registrationDate: company.registrationDate
          ? company.registrationDate.slice(0, 10)
          : '',
        registrationNumber: company.registrationNumber || '',
        tradeName: company.tradeName || '',
        businessStyle: company.businessStyle || '',
        isBmbe: company.isBmbe ?? false,
        bmbeRegistrationNumber: company.bmbeRegistrationNumber || '',
        fiscalYearType: company.fiscalYearType || 'calendar',
        fiscalYearStart: company.fiscalYearStart
          ? toMonthDay(company.fiscalYearStart.slice(0, 10))
          : '01-01',
        fiscalYearEnd: company.fiscalYearEnd
          ? toMonthDay(company.fiscalYearEnd.slice(0, 10))
          : '12-31',
      });
      setTinError(null);
    }
  }, [company, defaultFiscalYearEnd, defaultFiscalYearStart, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;
    if (!form.name.trim()) {
      onError('Name is required');
      return;
    }

    const tinValidationError = getTinValidationError(form.tin);
    if (tinValidationError) {
      setTinError(tinValidationError);
      return;
    }
    setTinError(null);

    const sanitizedTin = form.tin.replace(/\D/g, '');
    const fiscalYearStartValue =
      form.fiscalYearType === 'calendar'
        ? defaultFiscalYearStart
        : toFullDate(form.fiscalYearStart, currentYear);
    const fiscalYearEndValue =
      form.fiscalYearType === 'calendar'
        ? defaultFiscalYearEnd
        : toFullDate(form.fiscalYearEnd, currentYear);

    if (
      form.fiscalYearType === 'custom' &&
      (!validateMonthDay(form.fiscalYearStart) ||
        !validateMonthDay(form.fiscalYearEnd))
    ) {
      onError('Custom fiscal year must use valid MM-DD values.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || DEFAULT_COMPANY_DESCRIPTION,
        // Allow clearing fields by sending empty string
        address: form.address.trim(),
        tin: sanitizedTin,
        tel: form.tel.trim(),
        email: form.email.trim(),
        vatStatus: form.vatStatus.trim(),
        entityType: form.entityType.trim(),
        registrationDate: form.registrationDate || '',
        registrationNumber: form.registrationNumber.trim(),
        tradeName: form.tradeName.trim(),
        businessStyle: form.businessStyle.trim(),
        isBmbe: form.isBmbe,
        bmbeRegistrationNumber: form.isBmbe
          ? form.bmbeRegistrationNumber.trim()
          : '',
        fiscalYearType: form.fiscalYearType,
        fiscalYearStart: fiscalYearStartValue,
        fiscalYearEnd: fiscalYearEndValue,
      };
      const updated = await companyService.updateCompany(company.id, {
        ...payload,
      });
      onSuccess({
        ...company,
        name: updated.name,
        description: updated.description,
        address: updated.address ?? '',
        tin: updated.tin ?? '',
        tel: updated.tel ?? '',
        email: updated.email ?? '',
        logoPath: updated.logoPath ?? '',
        vatStatus: updated.vatStatus ?? '',
        entityType: updated.entityType ?? '',
        registrationDate: updated.registrationDate ?? '',
        registrationNumber: updated.registrationNumber ?? '',
        tradeName: updated.tradeName ?? '',
        businessStyle: updated.businessStyle ?? '',
        isBmbe: updated.isBmbe ?? false,
        bmbeRegistrationNumber: updated.bmbeRegistrationNumber ?? '',
        fiscalYearType: updated.fiscalYearType ?? 'calendar',
        fiscalYearStart: updated.fiscalYearStart ?? '',
        fiscalYearEnd: updated.fiscalYearEnd ?? '',
        updatedAt: updated.updatedAt,
      });
      onHide();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update company';
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!company) return null;
  const fiscalYearStartParts = splitMonthDay(form.fiscalYearStart);
  const fiscalYearEndParts = splitMonthDay(form.fiscalYearEnd);
  const fiscalYearStartDays = getDayOptions(fiscalYearStartParts.month);
  const fiscalYearEndDays = getDayOptions(fiscalYearEndParts.month);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Company</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <Form.Group controlId="companyName" className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </Form.Group>
          <Form.Group controlId="companyDescription" className="mb-3">
            <Form.Label>
              Description <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group controlId="companyAddress" className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
              placeholder="Enter address"
            />
          </Form.Group>
          <Form.Group controlId="companyTin" className="mb-3">
            <Form.Label>TIN</Form.Label>
            <Form.Control
              type="text"
              value={form.tin}
              onChange={(e) => {
                setForm((f) => ({ ...f, tin: e.target.value }));
                if (tinError) setTinError(null);
              }}
              onBlur={() => {
                const error = getTinValidationError(form.tin);
                if (error) {
                  setTinError(error);
                  return;
                }
                const formatted = formatTinForInputMemo(form.tin);
                setForm((f) => ({ ...f, tin: formatted }));
              }}
              placeholder={TIN_PLACEHOLDER}
            />
            {tinError && (
              <Form.Text className="text-danger">{tinError}</Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="companyTel" className="mb-3">
            <Form.Label>
              Tel <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.tel}
              onChange={(e) => setForm((f) => ({ ...f, tel: e.target.value }))}
              placeholder="Enter telephone number"
            />
          </Form.Group>
          <Form.Group controlId="companyEmail" className="mb-3">
            <Form.Label>
              Email <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              placeholder="Enter company email"
            />
          </Form.Group>
          <Form.Group controlId="companyVatStatus" className="mb-3">
            <Form.Label>VAT Status</Form.Label>
            <Form.Select
              value={form.vatStatus}
              onChange={(e) =>
                setForm((f) => ({ ...f, vatStatus: e.target.value }))
              }
            >
              {vatStatusOptions.map((opt) => (
                <option key={opt.value || '__empty'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="entityType" className="mb-3">
            <Form.Label>Type of Entity</Form.Label>
            <Form.Select
              value={form.entityType}
              onChange={(e) =>
                setForm((f) => ({ ...f, entityType: e.target.value }))
              }
            >
              <option value="">Select type of entity</option>
              {REGISTRATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="registrationDate" className="mb-3">
            <Form.Label>Date of Registration</Form.Label>
            <Form.Control
              type="date"
              value={form.registrationDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, registrationDate: e.target.value }))
              }
            />
          </Form.Group>

          <Form.Group controlId="registrationNumber" className="mb-3">
            <Form.Label>{registrationNumberLabel}</Form.Label>
            <Form.Control
              type="text"
              value={form.registrationNumber}
              onChange={(e) =>
                setForm((f) => ({ ...f, registrationNumber: e.target.value }))
              }
              placeholder={registrationNumberLabel}
            />
          </Form.Group>

          <Form.Group controlId="tradeName" className="mb-3">
            <Form.Label>Trade Name</Form.Label>
            <Form.Control
              type="text"
              value={form.tradeName}
              onChange={(e) =>
                setForm((f) => ({ ...f, tradeName: e.target.value }))
              }
              placeholder="e.g., Operating under the trade/business name of..."
            />
          </Form.Group>

          <Form.Group controlId="businessStyle" className="mb-3">
            <Form.Label>Business Style</Form.Label>
            <Form.Control
              type="text"
              value={form.businessStyle}
              onChange={(e) =>
                setForm((f) => ({ ...f, businessStyle: e.target.value }))
              }
              placeholder="e.g., Laundry Services, Transportation, I.T. Services"
            />
          </Form.Group>

          <Form.Group controlId="isBmbe" className="mb-3">
            <Form.Check
              type="switch"
              label="Registered as BMBE"
              checked={form.isBmbe}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  isBmbe: e.target.checked,
                  bmbeRegistrationNumber: e.target.checked
                    ? f.bmbeRegistrationNumber
                    : '',
                }))
              }
            />
          </Form.Group>

          {form.isBmbe && (
            <Form.Group controlId="bmbeRegistrationNumber" className="mb-3">
              <Form.Label>BMBE Registration No.</Form.Label>
              <Form.Control
                type="text"
                value={form.bmbeRegistrationNumber}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    bmbeRegistrationNumber: e.target.value,
                  }))
                }
                placeholder="Enter BMBE registration number"
              />
            </Form.Group>
          )}

          <Form.Group controlId="fiscalYearType" className="mb-3">
            <Form.Label>Fiscal Year</Form.Label>
            <Form.Select
              value={form.fiscalYearType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fiscalYearType: e.target.value as 'calendar' | 'custom',
                }))
              }
            >
              <option value="calendar">Calendar Year</option>
              <option value="custom">Custom Fiscal Year</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="fiscalYearStart" className="mb-3">
            <Form.Label>Fiscal Year Start Date</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                value={fiscalYearStartParts.month}
                onChange={(e) =>
                  setForm((f) => {
                    const nextMonth = e.target.value;
                    const previousDay = splitMonthDay(f.fiscalYearStart).day;
                    const nextDayOptions = getDayOptions(nextMonth);
                    const nextDay = nextDayOptions.includes(previousDay)
                      ? previousDay
                      : nextDayOptions[0] || '';
                    return {
                      ...f,
                      fiscalYearType: 'custom',
                      fiscalYearStart: buildMonthDay(nextMonth, nextDay),
                    };
                  })
                }
                disabled={submitting || form.fiscalYearType !== 'custom'}
              >
                <option value="">Month</option>
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                value={fiscalYearStartParts.day}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    fiscalYearType: 'custom',
                    fiscalYearStart: buildMonthDay(
                      splitMonthDay(f.fiscalYearStart).month,
                      e.target.value,
                    ),
                  }))
                }
                disabled={
                  submitting ||
                  form.fiscalYearType !== 'custom' ||
                  !fiscalYearStartParts.month
                }
              >
                <option value="">Day</option>
                {fiscalYearStartDays.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Form.Group>

          <Form.Group controlId="fiscalYearEnd" className="mb-3">
            <Form.Label>Fiscal Year End Date</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                value={fiscalYearEndParts.month}
                onChange={(e) =>
                  setForm((f) => {
                    const nextMonth = e.target.value;
                    const previousDay = splitMonthDay(f.fiscalYearEnd).day;
                    const nextDayOptions = getDayOptions(nextMonth);
                    const nextDay = nextDayOptions.includes(previousDay)
                      ? previousDay
                      : nextDayOptions[0] || '';
                    return {
                      ...f,
                      fiscalYearType: 'custom',
                      fiscalYearEnd: buildMonthDay(nextMonth, nextDay),
                    };
                  })
                }
                disabled={submitting || form.fiscalYearType !== 'custom'}
              >
                <option value="">Month</option>
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                value={fiscalYearEndParts.day}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    fiscalYearType: 'custom',
                    fiscalYearEnd: buildMonthDay(
                      splitMonthDay(f.fiscalYearEnd).month,
                      e.target.value,
                    ),
                  }))
                }
                disabled={
                  submitting ||
                  form.fiscalYearType !== 'custom' ||
                  !fiscalYearEndParts.month
                }
              >
                <option value="">Day</option>
                {fiscalYearEndDays.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            void handleSubmit(e as unknown as React.FormEvent);
          }}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditCompanyModal;
